<template>
<div
	class="app-image-upload"
	:class="{'app-image-upload--preview' : preview}"
>
	<label
		class="app-image-upload__input"
		:class="{ 'error': !!error }"
		@dragover.prevent
		@drop.prevent="uploadPhoto($event)"
		@dragenter.prevent="$el.classList.add('app-image-upload--dragover')"
		@dragleave.prevent="$el.classList.remove('app-image-upload--dragover')"
	>
		<input
			type="file"
			ref="fileInput"
			:multiple="multiple"
			@change="uploadPhoto($event)"
		>
		<p
			class="app-image-upload__placeholder"
			v-if="placeholder && !preview"
		>
			{{ placeholder }}
		</p>
		<small
			class="app-image-upload__error"
			v-if="error && !preview"
		>
			{{ error }}
		</small>
	</label>

	<template v-if="hasPreview">
		<slot name="preview" :props="{preview, loading}">
			<div
				v-if="preview"
				class="app-image-upload__preview"
			>
				<div
					class="preview"
					:class="{'preview--loading' : loading}"
					:style="`background-image: url(${preview});`"
				/>
				<svg
					class="remove"
					@click.prevent="remove"
					xmlns="http://www.w3.org/2000/svg"
					viewBox="0 0 14.4 16"
				>
					<path
						fill="#f4949a"
						d="M16.6,5.2H13.4V4.4A2.4,2.4,0,0,0,11,2H9.4A2.4,2.4,0,0,0,7,4.4v.8H3.8a.8.8,0,1,0,0,1.6h.8v8.8A2.4,2.4,0,0,0,7,18h6.4a2.4,2.4,0,0,0,2.4-2.4V6.8h.8a.8.8,0,1,0,0-1.6Zm-8-.8a.8.8,0,0,1,.8-.8H11a.8.8,0,0,1,.8.8v.8H8.6Zm5.6,11.2a.8.8,0,0,1-.8.8H7a.8.8,0,0,1-.8-.8V6.8h8Z"
						transform="translate(-3 -2)"
					/>
				</svg>
			</div>
		</slot>
	</template>
</div>
</template>

<script>
export default {
	props: {
		loading: Boolean,
		hasPreview: { type: Boolean, default: true },
		maxSize: { type: Number, default: 5000000 },
		type: String,
		error: String,
		placeholder: String,
		multiple: { type: Boolean, default: false },
		value: {
			type: [Array, Object, String, FileList, File],
			default: null,
			validator (val) {
				if (val) {
					const files = Array.from(val)
					return files.every(item => {
						if (typeof item === 'string') return true
						return item.type &&
							item.type.split('/')[0] === 'image'
					})
				}
			}
		}
	},

	data () {
		return {
			preview: ''
		}
	},

	watch: {
		value: {
			handler (val) {
				if (!val || this.multiple) {
					this.preview = ''
					return
				}

				if (typeof val === 'string') {
					this.preview = val
					return
				}

				if (!this.fileIsValid(val)) {
					this.preview = ''
					return
				}

				this.setPreview(val)
			},

			immediate: true
		}
	},

	methods: {
		uploadPhoto (e) {
			this.$el.classList.remove('app-image-upload--dragover')

			const file = e.target.files || e.dataTransfer.files

			if (file && file.length) {
				const files = Array.from(file)
				const invalidFile = files.some(item => !this.fileIsValid(item))
				if (invalidFile) {
					this.$emit('error', 'Invalid file type.')
					// this.$emit('input', null)
					return
				}

				const invalidSize = files.some(item => item.size > this.maxSize)
				if (invalidSize) {
					this.$emit('error', 'Image size must be 5MB or below.')
					// this.$emit('input', null)
					return
				}

				if (this.multiple) {
					this.$emit('input', Array.from(file))
				} else {
					this.$emit('input', file[0])
				}
				this.$emit('error', '')
			}

			e.target.type = 'text'
			e.target.type = 'file'
		},

		remove () {
			if (!this.$refs.fileInput) return
			this.$refs.fileInput.type = 'text'
			this.$refs.fileInput.type = 'file'
			this.preview = ''
			if (this.$listeners.remove) {
				this.$emit('remove', this.value)
			} else {
				this.$emit('input', null)
			}
		},

		fileIsValid (file) {
			if (this.type) {
				const acceptedImageTypes = this.type.slit(',')
				return file && acceptedImageTypes.includes(file.type)
			}

			return file && file.type.split('/')[0] === 'image'
		},

		setPreview (file) {
			if (!file && !this.hasPreview) return
			const reader = new FileReader()
			reader.onload = event => { this.preview = event.target.result }
			reader.readAsDataURL(file)
		}
	}
}
</script>

<style lang="scss" scoped>
.app-image-upload {
	display: inline-block;
	position: relative;
	border: 1px dashed #D8DEEB;
	background-color: #F8FAFD;
    user-select: none;

    &__error {
	    color: $danger;
    }

    .error {
	    border: 1px dashed $danger;
    }

	&--dragover, &:hover {
		box-shadow: inset 0 0 10px #D8DEEB;
	}

	&__input, &__preview {
		height: 100%;
		width: 100%;
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: center;
	}

	&__input {
		position: relative;
		border-radius: inherit;
		cursor: pointer;

		input {
			position: absolute;
			pointer-events: none;
			outline: none;
			top: 0;
			left: 0;
			opacity: 0;
			width: 0;
			height: 0;
		}
	}

	&__placeholder {
		color: #89B1D5;
		pointer-events: none;
	}

	&--preview {
		border: none;
		background-color: transparent;
	}

	&__preview {
		pointer-events: none;
		position: absolute;
		top: 0;
		left: 0;
		padding: 1.5em;

		.preview {
			pointer-events: none;
			background-size: contain;
			background-position: center;
			background-repeat: no-repeat;
			height: 100%;
			width: 100%;

			&--loading {
				opacity: 0.2;
			}
		}

		.remove {
			cursor: pointer;
			pointer-events: all;
			background-color: #fff;
			border: 1px solid $danger;
			position: absolute;
			top: 0px;
			right: -5px;
			width: 1.5em;
			height: 1.5em;
			padding: .2em;
			border-radius: 50%;
		}
	}

}
</style>
